/**
 * Filesystem-based enumerator for Claude slash commands on a *draft* session
 * (no live Claude agent yet). Replaces the 6s "spawn-just-to-list" round-trip
 * `agent-manager.listDraftCommands` would otherwise pay before the user has
 * even sent their first message.
 *
 * Sources covered (matches what the SDK's `q.supportedCommands()` returns from
 * disk — built-in slash commands like `/clear`, `/help`, `/model` come from
 * the SDK process itself and are deliberately omitted here; they reappear as
 * soon as the real session spawns on first user message):
 *
 *   ~/.claude/skills/<name>/SKILL.md                                  (global skills)
 *   <cwd>/.claude/skills/<name>/SKILL.md                              (project skills)
 *   ~/.claude/plugins/cache/<marketplace>/<plugin>/<ver>/skills/...   (plugin skills, prefixed `<plugin>:<name>`)
 */

import type { Dirent } from "node:fs";
import fs from "node:fs/promises";
import os from "node:os";
import path from "node:path";
import type { AgentSlashCommand } from "../agent-sdk-types.js";
import { listSkills } from "../../skills/scanner.js";

interface InstalledPluginsFile {
  version?: number;
  plugins?: Record<string, Array<{ installPath?: string }>>;
}

export async function enumerateClaudeDraftCommands(cwd: string): Promise<AgentSlashCommand[]> {
  const byName = new Map<string, AgentSlashCommand>();

  const [global, project, plugin] = await Promise.all([
    listSkills({ provider: "claude", scope: "global" }).catch(() => []),
    listSkills({ provider: "claude", scope: "project", workspaceRoot: cwd }).catch(() => []),
    listClaudePluginSkills().catch(() => []),
  ]);

  for (const entry of [...project, ...global, ...plugin]) {
    if (!byName.has(entry.name)) {
      byName.set(entry.name, {
        name: entry.name,
        description: entry.description,
        argumentHint: "",
      });
    }
  }

  return Array.from(byName.values()).sort((a, b) => a.name.localeCompare(b.name));
}

interface FlatSkillEntry {
  name: string;
  description: string;
}

async function listClaudePluginSkills(): Promise<FlatSkillEntry[]> {
  const pluginsRoot = path.join(os.homedir(), ".claude", "plugins");
  const installedPath = path.join(pluginsRoot, "installed_plugins.json");

  let raw: string;
  try {
    raw = await fs.readFile(installedPath, "utf8");
  } catch {
    return [];
  }

  let parsed: InstalledPluginsFile;
  try {
    parsed = JSON.parse(raw) as InstalledPluginsFile;
  } catch {
    return [];
  }

  const out: FlatSkillEntry[] = [];
  const plugins = parsed.plugins ?? {};

  for (const [key, installs] of Object.entries(plugins)) {
    if (!Array.isArray(installs) || installs.length === 0) continue;
    // Plugin keys look like "figma@claude-plugins-official" — the prefix the
    // slash menu uses is everything before the first `@`.
    const prefix = key.split("@")[0];
    if (!prefix) continue;

    // Use the most-recent install (last entry wins — matches Claude's own
    // resolution order for upgraded plugins).
    const install = installs[installs.length - 1];
    const installPath = install?.installPath;
    if (!installPath) continue;

    const skills = await readSkillFolder(path.join(installPath, "skills"));
    for (const skill of skills) {
      out.push({
        name: `${prefix}:${skill.name}`,
        description: skill.description,
      });
    }
  }

  return out;
}

async function readSkillFolder(dir: string): Promise<FlatSkillEntry[]> {
  let entries: Dirent[];
  try {
    entries = await fs.readdir(dir, { withFileTypes: true });
  } catch {
    return [];
  }

  const out: FlatSkillEntry[] = [];
  for (const entry of entries) {
    if (!entry.isDirectory() && !entry.isSymbolicLink()) continue;
    const skillPath = path.join(dir, entry.name, "SKILL.md");
    const description = await readDescriptionField(skillPath);
    if (description === null) continue;
    out.push({ name: entry.name, description });
  }
  return out;
}

const FRONTMATTER_RE = /^---\r?\n([\s\S]*?)\r?\n---/;

async function readDescriptionField(filePath: string): Promise<string | null> {
  let text: string;
  try {
    text = await fs.readFile(filePath, "utf8");
  } catch {
    return null;
  }
  const match = FRONTMATTER_RE.exec(text);
  if (!match) return "";
  const yaml = match[1] ?? "";
  for (const line of yaml.split(/\r?\n/)) {
    const m = /^description\s*:\s*(.*)$/.exec(line);
    if (!m) continue;
    let value = (m[1] ?? "").trim();
    if (
      (value.startsWith('"') && value.endsWith('"')) ||
      (value.startsWith("'") && value.endsWith("'"))
    ) {
      value = value.slice(1, -1);
    }
    return value;
  }
  return "";
}
